import React, { useMemo, useState } from "react";
import { ADVERTISER, OVERVIEW, PIN, advertiserRowClass } from "../../../../../core/theme/reportTheme";
import { PICKER_QUESTION, YOUR_BUSINESS } from "../../../../../core/data/advertiserOverviewText";
import { PICKER_COLUMNS, pickerDefaultCompare, searchPicker } from "../../../../../core/data/advertiserSorts";
import { sortRows, nextSort } from "../../../../../core/data/sortRows";
import SortButton from "../../../components/SortButton";

// "Which advertiser is this report for?": type to search, then Pin.
// Opens with your business first, then everyone in ranking order.
// cornerButton: the ⤢ button inline, or ✕ in the Change panel.
// highlightKeys: advertisers highlighted in yellow (from the Advertisers filter).
export default function PickerPanel({ result, onPin, cornerButton, highlightKeys = [] }) {
  const [text, setText] = useState("");
  const [sort, setSort] = useState(null);
  const rows = useMemo(
    () => sortRows(searchPicker(result.picker, text), sort, PICKER_COLUMNS, pickerDefaultCompare),
    [result.picker, text, sort]
  );
  const pinnedKey = result.pinned?.key;
  const sortProps = { sort, onSort: column => setSort(nextSort(sort, column, PICKER_COLUMNS)) };

  return (
    <div className={`${OVERVIEW.panel} flex-1`}>
      <div className={OVERVIEW.panelHeader}>
        <h3 className={OVERVIEW.panelTitle}>{PICKER_QUESTION}</h3>
        <div className="flex items-center gap-3 shrink-0">
          {sort && <button type="button" onClick={() => setSort(null)} className={OVERVIEW.link}>Back to default order</button>}
          {cornerButton}
        </div>
      </div>
      <div className="shrink-0 px-4 pb-2">
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input autoFocus className={ADVERTISER.input} placeholder="Type to search advertisers" value={text} onChange={e => setText(e.target.value)} />
      </div>
      <div className={OVERVIEW.panelScroll}>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={OVERVIEW.thFirst}><SortButton column="rank" label="#" {...sortProps} /></th>
              <th className={OVERVIEW.th}><SortButton column="advertiser" label="Advertiser" {...sortProps} /></th>
              <th className={OVERVIEW.th}><SortButton column="visibility" label="Visibility" {...sortProps} /></th>
              <th className={OVERVIEW.th} />
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const look = advertiserRowClass(r.key === pinnedKey, highlightKeys.includes(r.key));
              return (
              <tr key={r.key} className={look.row}>
                <td className={`${OVERVIEW.tdFirst} ${look.first}`}>{r.rank ?? "–"}</td>
                <td className={`${OVERVIEW.td} max-w-[20rem]`}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-slate-900" title={r.displayName}>{r.displayName}</span>
                    {r.isYourBusiness && <span className={PIN.tag}>{YOUR_BUSINESS}</span>}
                    {r.rank == null && <span className={OVERVIEW.muted}>not seen</span>}
                  </div>
                </td>
                <td className={`${OVERVIEW.td} font-semibold text-slate-900`}>{r.visibilityScore}%</td>
                <td className={`${OVERVIEW.td} text-right pr-4`}>
                  {r.key === pinnedKey
                    ? <span className={ADVERTISER.pinnedLabel}>Pinned</span>
                    : <button type="button" className={ADVERTISER.pinButton} onClick={() => onPin(r.key)}>Pin</button>}
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-sm text-slate-400 text-center">No advertiser matches "{text}".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
